import { computed, defineComponent } from "vue";
import type { IDecoratedNumberProps } from "./types";
import Icon from "../icon";
import ArrowUpIcon from "assets/img/svg/icons/arrow-up.svg?url";
import { isUndefined } from "@/utils/is";
import { uref } from "@/utils/reactive/uref";
import { tween } from "@/utils/animation/tween";
import { prettyNumber } from "@/utils/number/format";

const DecoratedNumber = defineComponent<IDecoratedNumberProps>({
  setup(props) {
    const inputValue = computed(
      () => (typeof props.value !== "undefined" ? uref(props.value) : 0) ?? 0
    );
    const numericValue =
      props.tween === false
        ? inputValue
        : tween(inputValue, {
            duration: 0.5,
          });
    const truncate = computed(() => {
      if (props.truncate !== undefined) {
        return props.truncate;
      }
      return props.unit !== "percent";
    });

    const unitChar = computed(() => {
      switch (props.unit) {
        case "percent":
          return "%";
        case "currency":
          return (props?.currency ? props.currency : "SEK") || "SEK";
        default:
          return null;
      }
    });

    const isNegative = computed(() => (numericValue.value ?? 0) < 0);

    const colorClass = computed(() =>
      props.noColorDecoration || Math.abs(numericValue.value) <= 0.0000001
        ? ""
        : isNegative.value
        ? "text-red-600"
        : "text-green-600"
    );
    const iconColor = computed(
      () =>
        props.iconColor ||
        (isNegative.value ? "rgb(224, 36, 36)" : "rgb(14, 159, 110)")
    );

    const precision = computed((): number =>
      isUndefined(props.precision) ? 2 : props.precision
    );

    const signPrefix = computed(() => {
      if (!props.showPlusMinus) return "";
      if (numericValue.value > 0) return "+";
      if (numericValue.value < 0) return "-";
      return "";
    });

    const withArrow = computed(() => !!props.arrow && numericValue.value !== 0);
    return () => {
      return (
        <div
          class={withArrow.value ? colorClass.value : ""}
          style={
            withArrow.value
              ? {
                  display: "grid",
                  gridTemplateColumns: "min-content max-content",
                  gap: props.iconGap || "0.25rem",
                }
              : {}
          }
        >
          {withArrow.value && (
            <div class="flex items-center justify-center">
              <Icon
                size={props.iconSize}
                flipVert={isNegative.value}
                fill={iconColor.value}
                src={ArrowUpIcon}
              />
            </div>
          )}
          <div class={`font-bold ${colorClass.value}`}>
            <span class={props.valueClass}>
              {signPrefix.value && <>{signPrefix.value}</>}
              {prettyNumber(
                props.showPlusMinus ? Math.abs(numericValue.value) : numericValue.value,
                props.formatOptions || {
                  truncate: truncate.value,
                  precision: precision.value,
                }
              )}
            </span>
            {unitChar.value ? (
              <span class={props.unitClass || ""}>
                &nbsp;{unitChar.value || ""}
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
      );
    };
  },
});

DecoratedNumber.props = [
  "value",
  "arrow",
  "unit",
  "signs",
  "showPlusMinus",
  "noColorDecoration",
  "valueClass",
  "unitClass",
  "precision",
  "maximumFractionalDigits",
  "autoPrecision",
  "iconSize",
  "iconColor",
  "iconGap",
  "currency",
  "tween",
  "formatOptions",
  "truncate",
];

export default DecoratedNumber;
